/* Auto open/closed status + day-row hover tooltips for the location-detail-hero section.
   Reads data-timezone / data-location-hours from [data-auto-status] and rewrites the status
   badge, detail text and colors (via the data-status attribute). No Alpine dependency. */
if (!window.Eurus.loadedScript.has('location-hours.js')) {
  window.Eurus.loadedScript.add('location-hours.js');

  (() => {
    const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const DAY_INDEX = {
      sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tues: 2, tue: 2,
      wednesday: 3, weds: 3, wed: 3, thursday: 4, thurs: 4, thur: 4, thu: 4,
      friday: 5, fri: 5, saturday: 6, sat: 6
    };
    const DASH = /[\u2010-\u2015\u2212-]/;
    const FULL_DAY_MINUTES = 24 * 60;
    const REFRESH_MS = 60000;

    // "8:30 AM", "8:30am", "8 pm", "20:00" -> minutes since midnight
    function parseTime(raw) {
      const text = String(raw || '').trim();
      let match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?$/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = match[2] ? parseInt(match[2], 10) : 0;
        if (hours > 12 || minutes > 59) return null;
        if (match[3].toLowerCase() === 'p' && hours < 12) hours += 12;
        if (match[3].toLowerCase() === 'a' && hours === 12) hours = 0;
        return hours * 60 + minutes;
      }
      match = text.match(/^(\d{1,2}):(\d{2})$/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        if (hours > 23 || minutes > 59) return null;
        return hours * 60 + minutes;
      }
      return null;
    }

    // "8:30 AM - 10:30 PM", "9:00 AM – 10:00 PM", "Closed" -> {open, close, overnight} | {closed} | null
    function parseRange(raw) {
      const text = String(raw || '').trim().toLowerCase();
      if (!text) return null;
      if (text === 'closed') return { closed: true };
      if (text === '24 hours' || text === '24/7' || text === 'open 24 hours') {
        return { open: 0, close: FULL_DAY_MINUTES };
      }
      const parts = text.split(DASH);
      if (parts.length !== 2) return null;
      const open = parseTime(parts[0]);
      const close = parseTime(parts[1]);
      if (open == null || close == null) return null;
      return { open, close, overnight: close <= open };
    }

    function buildSchedule(entries) {
      const schedule = new Array(7).fill(null);
      if (!Array.isArray(entries)) return schedule;
      entries.forEach((entry) => {
        const day = DAY_INDEX[String((entry && entry.day) || '').trim().toLowerCase()];
        if (day == null) return;
        schedule[day] = parseRange(entry.hours);
      });
      return schedule;
    }

    // Current weekday + minutes since midnight at the location (DST handled by Intl)
    function nowInZone(timezone) {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23'
        }).formatToParts(new Date());
        const value = (type) => (parts.find((part) => part.type === type) || {}).value;
        const dayIndex = DAY_INDEX[(value('weekday') || '').toLowerCase()];
        let hours = parseInt(value('hour'), 10);
        const minutes = parseInt(value('minute'), 10);
        if (dayIndex == null || Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        if (hours === 24) hours = 0;
        return { dayIndex, minutes: hours * 60 + minutes };
      } catch (error) {
        return null; // invalid timezone -> keep the static fallback
      }
    }

    // Checks today's window plus a previous-day window spilling past midnight.
    function openState(schedule, now) {
      for (let back = 0; back < 2; back++) {
        const range = schedule[(now.dayIndex - back + 7) % 7];
        if (!range || range.closed || range.open == null) continue;
        if (range.overnight) {
          if (back === 0 ? now.minutes >= range.open || now.minutes < range.close : now.minutes < range.close) {
            return { isOpen: true, closingAt: range.close };
          }
        } else if (back === 0 && now.minutes >= range.open && now.minutes < range.close) {
          return { isOpen: true, closingAt: range.close };
        }
      }
      return { isOpen: false };
    }

    function nextOpening(schedule, now) {
      for (let ahead = 0; ahead < 7; ahead++) {
        const dayIndex = (now.dayIndex + ahead) % 7;
        const range = schedule[dayIndex];
        if (!range || range.closed || range.open == null) continue;
        if (ahead === 0 && now.minutes >= range.open) continue;
        return { dayIndex, today: ahead === 0, minutes: range.open };
      }
      return null;
    }

    // "8:30am" for tooltips, "8:30 PM" for the status detail
    function formatTime(minutes, style) {
      const hours = Math.floor(minutes / 60) % 24;
      const mins = String(minutes % 60).padStart(2, '0');
      const period = hours >= 12 ? 'pm' : 'am';
      let display = hours % 12;
      if (display === 0) display = 12;
      if (style === 'lower') return `${display}:${mins}${period}`;
      return `${display}:${mins} ${period.toUpperCase()}`;
    }

    function applyTooltips(section) {
      section.querySelectorAll('.location-detail-hero__hours-row[data-day]').forEach((row) => {
        const day = (row.dataset.day || '').trim();
        const range = parseRange(row.dataset.hours);
        if (!day || !range) return;
        const plural = `${day}s`;
        if (range.closed) {
          row.dataset.tooltip = `This store is closed on ${plural}`;
        } else if (range.open === 0 && range.close === FULL_DAY_MINUTES) {
          row.dataset.tooltip = `This store is open 24 hours on ${plural}`;
        } else {
          row.dataset.tooltip = `This store is open at ${formatTime(range.open, 'lower')} and closes at ${formatTime(range.close, 'lower')} on ${plural}`;
        }
      });
    }

    function applyStatus(container) {
      if (!container.isConnected) return;
      let schedule;
      try {
        schedule = buildSchedule(JSON.parse(container.dataset.locationHours || '[]'));
      } catch (error) {
        return; // malformed JSON -> keep the static fallback
      }
      if (schedule.every((range) => !range || (!range.closed && range.open == null))) return;
      const now = nowInZone(container.dataset.timezone || 'America/Anchorage');
      if (!now) return;

      const badge = container.querySelector('.location-detail-hero__status-badge');
      const detail = container.querySelector('.location-detail-hero__status-detail');
      const dot = container.querySelector('.location-detail-hero__status-dot');
      const state = openState(schedule, now);

      if (state.isOpen) {
        container.dataset.status = 'open';
        if (badge) badge.textContent = 'OPEN';
        if (detail) detail.textContent = `Closes ${formatTime(state.closingAt, 'upper')}`;
        if (dot) dot.hidden = false;
      } else {
        container.dataset.status = 'closed';
        if (badge) badge.textContent = 'CLOSED';
        const next = nextOpening(schedule, now);
        const text = next ? `Opens ${formatTime(next.minutes, 'upper')}${next.today ? '' : ` ${DAY_NAMES[next.dayIndex]}`}` : '';
        if (detail) detail.textContent = text;
        if (dot) dot.hidden = !text;
      }
    }

    function init(root) {
      (root || document).querySelectorAll('[data-auto-status="true"]').forEach((container) => {
        if (container.dataset.hoursReady) return;
        container.dataset.hoursReady = 'true';
        applyTooltips(container.closest('.location-detail-hero') || document);
        applyStatus(container);
        setInterval(() => applyStatus(container), REFRESH_MS);
      });
    }

    init(document);
    document.addEventListener('shopify:section:load', (event) => init(event.target));
  })();
}
